import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Building2, Search, Users, Database, TrendingUp, ShieldCheck,
  Calendar, CheckCircle2, XCircle, Clock, AlertTriangle, ArrowLeft,
  Settings as SettingsIcon, History, Trash2,
} from 'lucide-react';
import { staffApi, useStaffAuth } from '../lib/staffAuth';
import { formatDateTime } from '../lib/constants';
import { PLAN_ORDER, PLANS, PLAN_STATUS_LABEL } from '../lib/plans';

const STATUS_TONE = {
  active:    { color: '#065F46', bg: '#ECFDF5', border: '#A7F3D0', icon: CheckCircle2 },
  trial:     { color: '#B45309', bg: '#FFFBEB', border: '#FDE68A', icon: Clock },
  expired:   { color: '#7F1D1D', bg: '#FEE2E2', border: '#F87171', icon: AlertTriangle },
  suspended: { color: '#6B7280', bg: '#F3F4F6', border: '#D1D5DB', icon: XCircle },
};

export default function PriviaxAdmin() {
  const { staffUser, refresh: refreshStaff, logout: staffLogout } = useStaffAuth();
  const navigate = useNavigate();
  const [kpis, setKpis] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editing, setEditing] = useState(null); // org object being edited
  const [tab, setTab] = useState('clientes'); // 'clientes' | 'leads'

  useEffect(() => { refreshStaff(); }, [refreshStaff]);

  const load = async () => {
    setLoading(true);
    try {
      const [k, o] = await Promise.all([
        staffApi.get('/superadmin/kpis'),
        staffApi.get('/superadmin/orgs'),
      ]);
      setKpis(k.data);
      setOrgs(o.data.organizations || []);
    } catch {
      toast.error('No se pudo cargar el backoffice');
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    let list = orgs;
    if (statusFilter !== 'all') list = list.filter((o) => o.plan_status_effective === statusFilter);
    if (query.trim()) {
      const q = query.trim().toLowerCase();
      list = list.filter((o) =>
        (o.razon_social || '').toLowerCase().includes(q) ||
        (o.rut || '').toLowerCase().includes(q),
      );
    }
    return list;
  }, [orgs, query, statusFilter]);

  return (
    <div className="min-h-screen bg-[#F7F8FA]" data-testid="priviax-admin">
      {/* Header propio (no usa el sidebar de clientes) */}
      <header className="bg-[#0B1220] text-white border-b border-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded bg-[#2A9B68] flex items-center justify-center">
              <ShieldCheck size={18} strokeWidth={1.75} />
            </div>
            <div>
              <div className="text-[10.5px] uppercase tracking-[0.14em] font-semibold text-[#7EE0AC]">
                Priviax · Backoffice interno
              </div>
              <h1 className="text-[18px] font-semibold tracking-[-0.01em]">
                Gestión de clientes y planes
              </h1>
            </div>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            {staffUser && (
              <div className="text-[11.5px] text-white/70 whitespace-nowrap">
                {staffUser.email}
              </div>
            )}
            <button
              onClick={() => { staffLogout(); navigate('/', { replace: true }); }}
              className="text-[12px] text-white/60 hover:text-white whitespace-nowrap"
              data-testid="admin-logout"
            >
              Cerrar sesión
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-6 space-y-6">
        {/* Tabs Clientes / Leads */}
        <div className="flex gap-1 border-b border-[#E5E7EB]" data-testid="admin-tabs">
          {[['clientes', 'Clientes'], ['leads', 'Leads · Diagnósticos anónimos']].map(([k, label]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-tab-${k}`}
              className={`px-4 py-2 text-[13px] font-medium border-b-2 -mb-px transition-colors ${tab === k ? 'border-[#143A34] text-[#143A34]' : 'border-transparent text-[#6B7280] hover:text-[#1C2329]'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'clientes' && (<>
        {/* KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <KpiCard label="Clientes totales" value={kpis?.total_orgs ?? '—'} icon={Building2} tone="#0F2A26" bg="#F0F7F5" />
          <KpiCard label="Activos" value={kpis?.active ?? '—'} icon={CheckCircle2} tone="#065F46" bg="#ECFDF5" />
          <KpiCard label="En prueba" value={kpis?.trial ?? '—'} icon={Clock} tone="#B45309" bg="#FFFBEB" />
          <KpiCard label="Vencidos" value={kpis?.expired ?? '—'} icon={AlertTriangle} tone="#7F1D1D" bg="#FEE2E2" />
          <KpiCard label="MRR estimado" value={kpis ? `${kpis.mrr_uf} UF` : '—'} icon={TrendingUp} tone="#0B1220" bg="#EEF2FF" highlight />
        </div>

        {/* Filtros */}
        <div className="bg-white border border-[#E5E7EB] p-3 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[240px]">
            <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              value={query} onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-[#D1D5DB] text-[13px]"
              placeholder="Buscar por razón social o RUT…"
              data-testid="admin-search"
            />
          </div>
          <select
            value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
            className="px-2 py-1.5 border border-[#D1D5DB] text-[13px] bg-white"
            data-testid="admin-filter-status"
          >
            <option value="all">Todos los estados</option>
            <option value="active">Activos</option>
            <option value="trial">En prueba</option>
            <option value="expired">Vencidos</option>
            <option value="suspended">Suspendidos</option>
          </select>
          <div className="text-[11.5px] text-[#6B7280] ml-auto">
            Mostrando <b className="text-[#1C2329]">{filtered.length}</b> de {orgs.length}
          </div>
        </div>

        {/* Tabla de clientes */}
        <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">
              <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
                <th className="px-3 py-2 font-semibold">Cliente</th>
                <th className="px-3 py-2 font-semibold">Creador</th>
                <th className="px-3 py-2 font-semibold">Plan</th>
                <th className="px-3 py-2 font-semibold">Estado</th>
                <th className="px-3 py-2 font-semibold">Vence</th>
                <th className="px-3 py-2 font-semibold text-right">Usuarios</th>
                <th className="px-3 py-2 font-semibold text-right">RATs</th>
                <th className="px-3 py-2 font-semibold">Última actividad</th>
                <th className="px-3 py-2 font-semibold text-right">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-[#6B7280]">Cargando…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={9} className="px-3 py-6 text-center text-[#6B7280]">Sin resultados</td></tr>
              ) : filtered.map((o) => {
                const tone = STATUS_TONE[o.plan_status_effective] || STATUS_TONE.trial;
                const Icon = tone.icon;
                return (
                  <tr key={o.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`admin-row-${o.id}`}>
                    <td className="px-3 py-2">
                      <div className="font-medium text-[#1C2329]">{o.razon_social}</div>
                      <div className="text-[11px] text-[#6B7280] font-mono">{o.rut}</div>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-[#374151]">
                      {o.creator_email ? (
                        <>
                          <div className="truncate max-w-[180px]" title={o.creator_email}>{o.creator_email}</div>
                          {o.creator_name && <div className="text-[11px] text-[#9CA3AF]">{o.creator_name}</div>}
                        </>
                      ) : <span className="text-[#9CA3AF]">—</span>}
                    </td>
                    <td className="px-3 py-2 text-[#1C2329]">{o.plan_label}</td>
                    <td className="px-3 py-2">
                      <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[11px] font-medium border"
                        style={{ color: tone.color, backgroundColor: tone.bg, borderColor: tone.border }}>
                        <Icon size={10} />
                        {PLAN_STATUS_LABEL[o.plan_status_effective] || o.plan_status_effective}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-[12px] text-[#374151]">
                      {o.plan_expires_at ? formatDateTime(o.plan_expires_at).slice(0, 10) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right text-[#374151]">
                      <span className="inline-flex items-center gap-1"><Users size={11} className="text-[#9CA3AF]" /> {o.users_count}</span>
                    </td>
                    <td className="px-3 py-2 text-right text-[#374151]">
                      <span className="inline-flex items-center gap-1"><Database size={11} className="text-[#9CA3AF]" /> {o.rats_count}</span>
                    </td>
                    <td className="px-3 py-2 text-[11.5px] text-[#6B7280]">
                      {o.last_activity_at ? formatDateTime(o.last_activity_at) : 'Sin actividad'}
                    </td>
                    <td className="px-3 py-2 text-right">
                      <button
                        onClick={() => setEditing(o)}
                        className="px-2 py-1 text-[11.5px] border border-[#143A34] text-[#143A34] hover:bg-[#143A34] hover:text-white transition"
                        data-testid={`admin-edit-${o.id}`}
                      >
                        Gestionar
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        </>)}

        {tab === 'leads' && <LeadsSection />}
      </div>

      {editing && (
        <EditPlanModal
          org={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

function LeadsSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [query, setQuery] = useState('');
  const [conv, setConv] = useState('all'); // 'all' | 'claimed' | 'unclaimed'
  useEffect(() => {
    (async () => {
      try {
        const r = await staffApi.get('/superadmin/anonymous-diagnostics');
        setData(r.data);
      } catch { /* silencioso */ }
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return null;
  const items = data?.items || [];
  const q = query.trim().toLowerCase();
  const filteredItems = items.filter((it) => {
    if (conv === 'claimed' && !it.claimed_at) return false;
    if (conv === 'unclaimed' && it.claimed_at) return false;
    if (!q) return true;
    const c = it.contact || {};
    return (c.razon_social || '').toLowerCase().includes(q)
        || (c.rut || '').toLowerCase().includes(q)
        || (c.email || '').toLowerCase().includes(q);
  });
  const display = showAll ? filteredItems : filteredItems.slice(0, 10);
  return (
    <div className="space-y-3 pb-10" data-testid="leads-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-[16px] font-semibold text-[#1C2329]">Leads · Diagnósticos anónimos</h2>
          <div className="text-[12px] text-[#6B7280] mt-0.5">
            Personas que hicieron el diagnóstico gratis desde el landing sin crear cuenta.
          </div>
        </div>
        <div className="flex gap-3 text-[12px]">
          <span className="px-2 py-1 bg-[#EEF2FF] text-[#0B1220]"><b>{data?.total ?? 0}</b> total</span>
          <span className="px-2 py-1 bg-[#ECFDF5] text-[#065F46]"><b>{data?.claimed ?? 0}</b> convertidos</span>
          <span className="px-2 py-1 bg-[#FFFBEB] text-[#B45309]"><b>{data?.unclaimed ?? 0}</b> sin cuenta</span>
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] p-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            className="w-full pl-8 pr-3 py-1.5 border border-[#D1D5DB] text-[13px]"
            placeholder="Buscar por empresa, RUT o correo…"
            data-testid="leads-search"
          />
        </div>
        <select
          value={conv} onChange={(e) => setConv(e.target.value)}
          className="px-2 py-1.5 border border-[#D1D5DB] text-[13px] bg-white"
          data-testid="leads-filter-conv"
        >
          <option value="all">Todos</option>
          <option value="claimed">Convertidos</option>
          <option value="unclaimed">Sin cuenta</option>
        </select>
        <div className="text-[11.5px] text-[#6B7280] ml-auto">
          Mostrando <b className="text-[#1C2329]">{filteredItems.length}</b> de {items.length}
        </div>
      </div>
      <div className="bg-white border border-[#E5E7EB] overflow-x-auto">
        <table className="w-full text-[13px]">
          <thead className="bg-[#FAFBFC] border-b border-[#E5E7EB]">
            <tr className="text-left text-[11px] uppercase tracking-wider text-[#6B7280]">
              <th className="px-3 py-2 font-semibold">Empresa</th>
              <th className="px-3 py-2 font-semibold">Correo</th>
              <th className="px-3 py-2 font-semibold text-right">Score</th>
              <th className="px-3 py-2 font-semibold">Nivel</th>
              <th className="px-3 py-2 font-semibold">Fecha</th>
              <th className="px-3 py-2 font-semibold">¿Convertido?</th>
            </tr>
          </thead>
          <tbody>
            {display.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-6 text-center text-[#6B7280]">Todavía no hay leads registrados.</td></tr>
            ) : display.map((it) => {
              const c = it.contact || {};
              return (
                <tr key={it.id} className="border-t border-[#F3F4F6] hover:bg-[#FAFBFC]" data-testid={`lead-row-${it.id}`}>
                  <td className="px-3 py-2 text-[#1C2329]">
                    {c.razon_social || <span className="text-[#9CA3AF]">—</span>}
                    {c.rut && <div className="text-[11px] text-[#6B7280] font-mono">{c.rut}</div>}
                  </td>
                  <td className="px-3 py-2 text-[12px] text-[#374151]">
                    {c.email || <span className="text-[#9CA3AF]">Sin correo</span>}
                    {c.telefono && <div className="text-[11px] text-[#9CA3AF]">{c.telefono}</div>}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-semibold text-[#1C2329]">{it.score}</td>
                  <td className="px-3 py-2 text-[12px] text-[#374151] capitalize">{it.level}</td>
                  <td className="px-3 py-2 text-[11.5px] text-[#6B7280]">{formatDateTime(it.created_at)}</td>
                  <td className="px-3 py-2 text-[12px]">
                    {it.claimed_at ? (
                      <span className="inline-flex items-center gap-1 text-[#065F46]">
                        <CheckCircle2 size={11} /> Sí
                      </span>
                    ) : <span className="text-[#9CA3AF]">No</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      {filteredItems.length > 10 && !showAll && (
        <button onClick={() => setShowAll(true)} className="text-[12.5px] text-[#143A34] hover:underline">
          Ver todos los {filteredItems.length} leads →
        </button>
      )}
    </div>
  );
}

function KpiCard({ label, value, icon: Icon, tone, bg, highlight }) {
  return (
    <div className="border border-[#E5E7EB] px-3 py-3 bg-white" data-testid={`kpi-${label.toLowerCase().replace(/\s/g, '-')}`}>
      <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-wider font-medium" style={{ color: tone, opacity: 0.75 }}>
        <Icon size={11} /> {label}
      </div>
      <div className={`font-semibold leading-none tracking-tight mt-1.5 ${highlight ? 'text-[24px]' : 'text-[22px]'}`} style={{ color: tone }}>
        {value}
      </div>
      <div className="text-[10px] mt-1" style={{ color: tone, opacity: 0.6, backgroundColor: bg, display: 'inline-block', padding: '1px 6px' }}>
        {highlight ? 'Estimado mensual' : ''}
      </div>
    </div>
  );
}

function EditPlanModal({ org, onClose, onSaved }) {
  const [plan, setPlan] = useState(org.plan);
  const [status, setStatus] = useState(org.plan_status);
  const [expires, setExpires] = useState(org.plan_expires_at ? org.plan_expires_at.slice(0, 10) : '');
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState('');
  const [tab, setTab] = useState('plan'); // 'plan' | 'historial' | 'eliminar'

  const save = async () => {
    setSaving(true);
    try {
      const payload = { plan, plan_status: status };
      payload.plan_expires_at = expires || null;
      await staffApi.patch(`/superadmin/orgs/${org.id}`, payload);
      toast.success('Plan actualizado');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo actualizar');
    } finally { setSaving(false); }
  };

  const extend = async (days) => {
    setSaving(true);
    try {
      await staffApi.patch(`/superadmin/orgs/${org.id}`, { plan_status: 'active', extend_days: days });
      toast.success(`Extendido ${days} días`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo extender');
    } finally { setSaving(false); }
  };

  const deleteOrg = async () => {
    setDeleting(true);
    try {
      const { data } = await staffApi.delete(`/superadmin/orgs/${org.id}`);
      const total = Object.values(data.deleted_counts || {}).reduce((a, b) => a + b, 0);
      toast.success(`Empresa eliminada · ${total} registros borrados`);
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'No se pudo eliminar');
    } finally { setDeleting(false); }
  };

  const historyCount = Array.isArray(org.plan_events) ? org.plan_events.length : 0;

  const tabs = [
    { key: 'plan',      label: 'Plan y estado', icon: SettingsIcon },
    { key: 'historial', label: `Historial${historyCount ? ` · ${historyCount}` : ''}`, icon: History },
    { key: 'eliminar',  label: 'Eliminar',      icon: Trash2, danger: true },
  ];

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-white border border-[#E5E7EB] w-full max-w-xl max-h-[90vh] shadow-2xl flex flex-col"
        onClick={(e) => e.stopPropagation()}
        data-testid="admin-edit-modal"
      >
        {/* Header fijo */}
        <div className="px-5 py-4 border-b border-[#E5E7EB] shrink-0">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[10.5px] uppercase tracking-wider text-[#6B7280]">Gestionar cliente</div>
              <div className="text-[16px] font-semibold text-[#1C2329] mt-0.5 truncate">{org.razon_social}</div>
              <div className="text-[11.5px] text-[#6B7280] font-mono">{org.rut}</div>
              {org.creator_email && (
                <div className="mt-1 text-[11.5px] text-[#6B7280] truncate">
                  Creador: <span className="text-[#374151]">{org.creator_email}</span>
                  {org.creator_name && <span className="text-[#9CA3AF]"> · {org.creator_name}</span>}
                </div>
              )}
            </div>
            <button
              type="button" onClick={onClose}
              className="shrink-0 text-[#9CA3AF] hover:text-[#1C2329] p-1 -m-1"
              data-testid="admin-close-x"
              aria-label="Cerrar"
            >
              <XCircle size={18} strokeWidth={1.6} />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-[#E5E7EB] bg-[#FAFBFC] shrink-0 flex overflow-x-auto">
          {tabs.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTab(t.key)}
                data-testid={`admin-tab-${t.key}`}
                className={`shrink-0 px-4 py-2.5 text-[12.5px] font-medium border-b-2 transition-colors inline-flex items-center gap-1.5 ${
                  active
                    ? (t.danger
                        ? 'border-[#B91C1C] text-[#B91C1C] bg-white'
                        : 'border-[#143A34] text-[#143A34] bg-white')
                    : 'border-transparent text-[#6B7280] hover:text-[#1C2329]'
                }`}
              >
                <Icon size={13} strokeWidth={1.8} />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Contenido con scroll */}
        <div className="flex-1 overflow-y-auto" data-testid="admin-modal-body">
          {tab === 'plan' && (
            <div className="px-5 py-4 space-y-4">
              {/* Plan */}
              <div>
                <label className="block text-[11.5px] font-medium text-[#374151] mb-1">Plan comercial</label>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                  {PLAN_ORDER.map((k) => (
                    <button key={k}
                      onClick={() => setPlan(k)}
                      className={`px-3 py-2 border text-[12.5px] text-left transition ${
                        plan === k ? 'border-[#143A34] bg-[#F0F7F5] text-[#0F2A26]' : 'border-[#D1D5DB] bg-white text-[#374151] hover:border-[#9CA3AF]'
                      }`}
                      data-testid={`admin-plan-${k}`}
                    >
                      <div className="font-semibold">{PLANS[k].name}</div>
                      <div className="text-[10.5px] text-[#6B7280]">{PLANS[k].price}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Estado */}
              <div>
                <label className="block text-[11.5px] font-medium text-[#374151] mb-1">Estado</label>
                <select
                  value={status} onChange={(e) => setStatus(e.target.value)}
                  className="w-full px-2 py-1.5 border border-[#D1D5DB] text-[13px] bg-white"
                  data-testid="admin-status-select"
                >
                  <option value="trial">Prueba (Trial)</option>
                  <option value="active">Activo</option>
                  <option value="suspended">Suspendido</option>
                </select>
              </div>

              {/* Fecha de vencimiento */}
              <div>
                <label className="block text-[11.5px] font-medium text-[#374151] mb-1">
                  Fecha de vencimiento
                  <span className="text-[10.5px] text-[#6B7280] font-normal ml-1">(dejar vacío = sin vencimiento)</span>
                </label>
                <div className="flex flex-wrap items-center gap-2">
                  <input type="date"
                    value={expires}
                    onChange={(e) => setExpires(e.target.value)}
                    className="flex-1 min-w-[160px] px-2 py-1.5 border border-[#D1D5DB] text-[13px]"
                    data-testid="admin-expires-input"
                  />
                  {expires && (
                    <button
                      onClick={() => setExpires('')}
                      className="text-[11px] text-[#6B7280] hover:text-[#B91C1C]"
                    >Limpiar</button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  <button onClick={() => extend(30)} disabled={saving}
                    className="text-[11.5px] px-2 py-1 border border-[#143A34] text-[#143A34] hover:bg-[#143A34] hover:text-white transition disabled:opacity-50"
                    data-testid="admin-extend-30">
                    <Calendar size={10} className="inline mr-1" /> Activar + 30 días
                  </button>
                  <button onClick={() => extend(90)} disabled={saving}
                    className="text-[11.5px] px-2 py-1 border border-[#143A34] text-[#143A34] hover:bg-[#143A34] hover:text-white transition disabled:opacity-50"
                    data-testid="admin-extend-90">
                    <Calendar size={10} className="inline mr-1" /> Activar + 90 días
                  </button>
                  <button onClick={() => extend(365)} disabled={saving}
                    className="text-[11.5px] px-2 py-1 border border-[#143A34] text-[#143A34] hover:bg-[#143A34] hover:text-white transition disabled:opacity-50"
                    data-testid="admin-extend-365">
                    <Calendar size={10} className="inline mr-1" /> Activar + 1 año
                  </button>
                </div>
              </div>
            </div>
          )}

          {tab === 'historial' && (
            <div className="px-5 py-4" data-testid="admin-historial-tab">
              {historyCount === 0 ? (
                <div className="text-[13px] text-[#6B7280] text-center py-6">
                  Sin cambios de plan registrados todavía.
                </div>
              ) : (
                <div className="space-y-1.5">
                  {org.plan_events.slice().reverse().map((ev, i) => (
                    <div key={i} className="text-[12.5px] text-[#374151] flex items-start gap-2 border-b border-[#F3F4F6] pb-1.5">
                      <span className="text-[#9CA3AF] font-mono shrink-0 w-[75px]">{(ev.created_at || '').slice(0, 10)}</span>
                      <span className="min-w-0 flex-1">
                        <b>{ev.metadata?.next?.plan_status || 'update'}</b>
                        {ev.metadata?.extend_days ? ` · +${ev.metadata.extend_days} días` : ''}
                        {ev.metadata?.next?.plan_expires_at ? ` · hasta ${String(ev.metadata.next.plan_expires_at).slice(0, 10)}` : ''}
                        <div className="text-[11px] text-[#9CA3AF] mt-0.5">por {ev.user_email}</div>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {tab === 'eliminar' && (
            <div className="px-5 py-4 bg-[#FEF2F2]" data-testid="admin-eliminar-tab">
              <div className="flex items-start gap-2 mb-3">
                <AlertTriangle size={16} className="text-[#B91C1C] shrink-0 mt-0.5" strokeWidth={2} />
                <div>
                  <div className="text-[13px] font-semibold text-[#991B1B]">Eliminación permanente</div>
                  <div className="text-[12px] text-[#7F1D1D] leading-snug mt-1">
                    Al eliminar la empresa se borrarán de forma permanente su RAT, documentos,
                    brechas, DPIA, solicitudes ARCO+, bitácora y los usuarios cuya organización
                    primaria sea esta. Los usuarios invitados desde otra organización conservan
                    su cuenta y sólo pierden esta membresía.
                  </div>
                </div>
              </div>
              <div className="mt-3">
                <label className="block text-[11.5px] font-medium text-[#7F1D1D] mb-1">
                  Escriba el RUT <b className="font-mono">{org.rut}</b> para confirmar
                </label>
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2">
                  <input
                    type="text"
                    value={confirmDelete}
                    onChange={(e) => setConfirmDelete(e.target.value)}
                    placeholder={org.rut}
                    className="flex-1 min-w-0 px-2 py-1.5 border border-[#FCA5A5] text-[12.5px] bg-white"
                    data-testid="admin-delete-confirm-input"
                  />
                  <button
                    onClick={deleteOrg}
                    disabled={deleting || confirmDelete.trim() !== org.rut}
                    className="shrink-0 px-3 py-1.5 text-[12.5px] bg-[#B91C1C] hover:bg-[#991B1B] text-white disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-1.5"
                    data-testid="admin-delete-org"
                  >
                    <Trash2 size={12} strokeWidth={2} />
                    {deleting ? 'Eliminando…' : 'Eliminar empresa'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer fijo (solo pestaña Plan) */}
        {tab === 'plan' && (
          <div className="px-5 py-3 border-t border-[#E5E7EB] flex justify-end gap-2 bg-[#FAFBFC] shrink-0">
            <button onClick={onClose} className="px-3 py-1.5 text-[12.5px] border border-[#D1D5DB] text-[#374151] bg-white hover:bg-[#F3F4F6]"
              data-testid="admin-cancel">Cancelar</button>
            <button onClick={save} disabled={saving}
              className="px-3 py-1.5 text-[12.5px] bg-[#143A34] hover:bg-[#0F2A26] text-white disabled:opacity-50"
              data-testid="admin-save">
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
